/**
 * Activity Logs API Client
 */

import apiClient from './client';

export interface ActivityLog {
  id: string;
  tenant_id: string;
  user_id?: string;
  user_email?: string;
  user_name?: string;
  action: string;
  entity_type: string;
  entity_id?: string;
  entity_name?: string;
  changes?: string;
  metadata?: string;
  ip_address?: string;
  created_at: string;
}

export interface ActivityLogsResponse {
  items: ActivityLog[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ActivityStatsResponse {
  period_days: number;
  total: number;
  by_action: { action: string; count: number }[];
  by_entity: { entity_type: string; count: number }[];
  by_user: { user_email: string; user_name: string; count: number }[];
  daily: { date: string; count: number }[];
}

export interface ActivityLogsParams {
  page?: number;
  limit?: number;
  entity_type?: string;
  entity_id?: string;
  action?: string;
  user_id?: string;
  start_date?: string;
  end_date?: string;
}

// Action labels for display
export const ACTION_LABELS: Record<string, string> = {
  create: 'Created',
  update: 'Updated',
  delete: 'Deleted',
  view: 'Viewed',
  login: 'Logged in',
  logout: 'Logged out',
  complete: 'Completed',
  archive: 'Archived',
  restore: 'Restored',
  assign: 'Assigned',
  unassign: 'Unassigned',
};

// Entity type labels
export const ENTITY_LABELS: Record<string, string> = {
  template: 'Template',
  instance: 'Task',
  category: 'Category',
  tag: 'Tag',
  user: 'User',
  project: 'Project',
  note: 'Note',
  milestone: 'Milestone',
  stage: 'Stage',
  subtask: 'Subtask',
  domain: 'Business Domain',
};

export const activityLogsApi = {
  async list(params: ActivityLogsParams = {}): Promise<ActivityLogsResponse> {
    const { data } = await apiClient.get('/v1/activity-logs', { params });
    return data;
  },

  async getEntityHistory(
    entityType: string,
    entityId: string,
    limit = 20
  ): Promise<{ items: ActivityLog[] }> {
    const { data } = await apiClient.get(
      `/v1/activity-logs/entity/${entityType}/${entityId}`,
      { params: { limit } }
    );
    return data;
  },

  async getUserHistory(
    userId: string,
    limit = 50
  ): Promise<{ items: ActivityLog[] }> {
    const { data } = await apiClient.get(`/v1/activity-logs/user/${userId}`, {
      params: { limit },
    });
    return data;
  },

  async getStats(days = 7): Promise<ActivityStatsResponse> {
    const { data } = await apiClient.get('/v1/activity-logs/stats', {
      params: { days },
    });
    return data;
  },
};

export default activityLogsApi;
